import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import MapGL, {
    Source,
    Layer,
    LayerProps,
    MapRef,
    ViewState
} from 'react-map-gl/mapbox';
import Supercluster from 'supercluster';
import { throttle } from 'lodash';
import { getLocaleRegionBoundingBox } from '@/tasks/util/map-helpers';
import {
    LocationAnalyticsProvider,
    useLocationAnalytics
} from '../providers/location-analytics-provider';
import { useRegion } from '@/common/providers/region-provider';
import { FeatureCollection } from '../util/to-feature-collection';
import { useAnalyticsTranslation } from '../hooks';
import { ChartContainer } from './chart-container';
import { UIIconName, UITextType } from '@/ui';
import { orange } from '@/ui/styles';
import { MAPBOX_TOKEN } from '@/config';

// Supercluster options
const radius = 10;
const maxZoom = 20;
const minZoom = 1;
const minPoints = 1;
const extent = 1024;
const nodeSize = 64;

type InternalLocationHeatmapProps = {
    featureCollection: FeatureCollection;
};
function InternalLocationHeatmap(
    props: InternalLocationHeatmapProps
): ReactElement {
    const { featureCollection } = props;

    const { resolvedRegion } = useRegion();
    const mapRef = useRef<MapRef | null>(null);
    const superclusterRef = useRef<Supercluster>(null);

    const [mapLoaded, setMapLoaded] = useState(false);
    const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection>({
        type: 'FeatureCollection',
        features: []
    });

    useEffect(() => {
        if (!mapLoaded) {
            return;
        }
        superclusterRef.current = new Supercluster({
            radius,
            maxZoom,
            minZoom,
            minPoints,
            extent,
            nodeSize
        });
        superclusterRef.current.load(featureCollection.features);
    }, [mapLoaded, featureCollection]);

    function calcFeatures() {
        if (mapRef.current && superclusterRef.current) {
            const zoom = mapRef.current.getZoom();
            const bounds = mapRef.current.getBounds();

            const topRightCoords = {
                lat: bounds?._ne.lat ?? 0,
                lng: bounds?._ne.lng ?? 0
            };
            const bottomLeftCoords = {
                lat: bounds?._sw.lat ?? 0,
                lng: bounds?._sw.lng ?? 0
            };
            const bBox: GeoJSON.BBox = [
                bottomLeftCoords.lng, // westLng - min lng
                bottomLeftCoords.lat, // southLat - min lat
                topRightCoords.lng, // eastLng - max lng
                topRightCoords.lat // northLat - max lat
            ];

            const features = superclusterRef.current?.getClusters(
                bBox,
                Math.floor(zoom)
            );

            const geoJsonData = {
                type: 'FeatureCollection',
                features
            };
            setGeoJsonData(geoJsonData as GeoJSON.FeatureCollection);
        } else {
            console.error('Failed to get map ref or super cluster ref');
        }
    }
    const _calcFeatures = useRef(
        throttle(calcFeatures, 200, { trailing: true })
    );

    const heatmapLayer: LayerProps = useMemo(() => {
        const counts = geoJsonData.features.map((f) => {
            type Property = {
                point_count: number;
            };
            const properties = f.properties as Property | undefined;
            return properties?.point_count || 1;
        });
        counts.sort((a, b) => a - b);

        const p5 = counts[Math.floor(counts.length * 0.05)] || 1;
        const p95 = counts[Math.floor(counts.length * 0.95)] || 10;

        return {
            id: 'heatmap',
            type: 'heatmap',
            paint: {
                'heatmap-weight': [
                    'interpolate',
                    ['linear'],
                    ['get', 'point_count'],
                    p5,
                    0.2,
                    p95,
                    1
                ],
                'heatmap-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    7,
                    1,
                    9,
                    0.4
                ]
            }
        };
    }, [geoJsonData]);

    const initialViewState = useMemo(() => {
        if (featureCollection.features.length === 0) {
            const defaultBounds = getLocaleRegionBoundingBox(resolvedRegion);

            return {
                bounds: [
                    defaultBounds.sw.lon,
                    defaultBounds.sw.lat,
                    defaultBounds.ne.lon,
                    defaultBounds.ne.lat
                ]
            };
        }

        if (featureCollection.features.length === 1) {
            const [lng, lat] =
                featureCollection.features[0].geometry.coordinates;

            return {
                latitude: lat,
                longitude: lng,
                zoom: 15
            };
        }

        // Calculate the bounds of the data
        let minLng = Infinity,
            maxLng = -Infinity;
        let minLat = Infinity,
            maxLat = -Infinity;

        // calculate rect
        featureCollection.features.forEach((feature) => {
            const [lng, lat] = feature.geometry.coordinates;
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        });

        const paddingPercent = 0.2; // 20% padding
        const lngPadding = (maxLng - minLng) * paddingPercent;
        const latPadding = (maxLat - minLat) * paddingPercent;

        return {
            bounds: [
                minLng - lngPadding,
                minLat - latPadding,
                maxLng + lngPadding,
                maxLat + latPadding
            ]
        };
    }, [featureCollection, resolvedRegion]);

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                //borderRadius: 8,
                overflow: 'hidden'
                //border: `1px solid ${concrete}`
            }}
        >
            <MapGL
                ref={mapRef}
                initialViewState={initialViewState as ViewState}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                projection={{ name: 'mercator' }}
                mapboxAccessToken={MAPBOX_TOKEN}
                onMove={_calcFeatures.current}
                onLoad={() => {
                    setMapLoaded(true);
                    setTimeout(() => {
                        calcFeatures();
                    }, 1000);
                }}
            >
                <Source type="geojson" data={geoJsonData}>
                    <Layer {...heatmapLayer} />
                </Source>
            </MapGL>
        </div>
    );
}

function InternalLocationHeatmapWrapper(): ReactElement {
    const { t } = useAnalyticsTranslation();
    const {
        featureCollection,
        status,
        progress,
        dateString,
        isWithinDateRange,
        isDateRangeAdjusted,
        maxDaysAgoAllowed
    } = useLocationAnalytics();

    const loadingText = useMemo(() => {
        if (status === 'pending') {
            return t('components.location_heatmap.loading', { progress });
        }
        return '';
    }, [status, progress, t]);

    const errorText = useMemo(() => {
        if (!isWithinDateRange) {
            return t('components.location_heatmap.error_out_of_range', {
                days: maxDaysAgoAllowed
            });
        }
        return undefined;
    }, [isWithinDateRange, maxDaysAgoAllowed, t]);

    const subtitleProps = useMemo<{
        subtitle?: string | null | undefined;
        subtitleType?: UITextType;
        subtitleIcon?: UIIconName;
        subtitleIconColor?: string;
    }>(() => {
        if (isDateRangeAdjusted) {
            return {
                subtitle: t('components.location_heatmap.subtitle_restricted', {
                    dateString,
                    days: maxDaysAgoAllowed
                }),
                subtitleType: 'warning',
                subtitleIcon: 'alert-triangle',
                subtitleIconColor: orange
            };
        } else {
            return {
                subtitle: dateString
            };
        }
    }, [dateString, maxDaysAgoAllowed, isDateRangeAdjusted, t]);

    return (
        <ChartContainer
            title={t('components.location_heatmap.title')}
            titleWeight="medium"
            {...subtitleProps}
            description={t('components.location_heatmap.description')}
            loading={status === 'pending'}
            loadingText={loadingText}
            error={status === 'error'}
            errorText={errorText}
            contentPadding={0}
        >
            <InternalLocationHeatmap
                featureCollection={
                    featureCollection || {
                        type: 'FeatureCollection',
                        features: []
                    }
                }
            />
        </ChartContainer>
    );
}

export function LocationHeatmap(): ReactElement {
    return (
        <LocationAnalyticsProvider>
            <InternalLocationHeatmapWrapper />
        </LocationAnalyticsProvider>
    );
}
